import { Polygon } from "@react-google-maps/api";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCitiesBoundaries, setRegionsUserCounts, setSelectedCity, setSelectedRegion, setSelectedTown, setUserBoundariesList } from "../../features/boundaries/boundariesSlice";

import CustomInfoWindow from "./CustomInfoWindow";
import { setFilteredUsersLocations } from "../../features/locs/locsSlice";

// Polygon için seçenekler (hover, aktif vs.)
const options = {
  strokeColor: "#010691",
  strokeOpacity: 1,
  strokeWeight: 1,
  fillColor: "#010691",
  fillOpacity: 0.15,
  clickable: true,
  zIndex: -999,
};

const hoveredOptions = {
  strokeColor: "#010691",
  strokeOpacity: 1,
  strokeWeight: 1,
  fillColor: "#010691",
  fillOpacity: 0.4,
  clickable: true,
  zIndex: -999,
};

const activeOptions = {
  strokeColor: "#010691",
  strokeOpacity: 1,
  strokeWeight: 1,
  fillColor: "transparent",
  clickable: false,
  zIndex: -999,
};

const RegionsBoundaries = () => {
  const dispatch = useDispatch();
    
    const { selectedRegion, regionsBoundariesList, regionsUserCounts, userBoundariesList } = useSelector((state) => state.boundaries);
  const {allUsersLocationList} = useSelector(state => state.locs)

  const workerRef = useRef()

  // hoveredRegion state'inde; mouse polygon üzerine geldiğinde bölge bilgileri ve güncel konum tutulur.
  // Örnek: { regionId, id, regionName, position: { lat, lng } }
  const [hoveredRegion, setHoveredRegion] = useState(null);

  // Polygon üzerine mouse geldiğinde hoveredRegion state'ini güncelliyoruz.
  const handlePolygonMouseOver = useCallback((item, userCount, event) => {
    setHoveredRegion({
      regionId: item.regionId,
      id: item.id,
      regionName: item.regionName,
      position: { lat: event.latLng.lat(), lng: event.latLng.lng() },
      userCount: userCount
    });
  }, []);

  // Mouse move ile info window konumunu güncelliyoruz.
  const handlePolygonMouseMove = useCallback((event) => {
    setHoveredRegion((prev) =>
      prev ? { ...prev, position: { lat: event.latLng.lat(), lng: event.latLng.lng() } } : null
    );
  }, []);

  const handlePolygonMouseOut = useCallback(() => {
    setHoveredRegion(null);
  }, []);



  useEffect(() => {
    // Eğer marker veya region verisi yoksa çalıştırma.
    if (allUsersLocationList.length > 0 || regionsBoundariesList.length === 7){
    
    // Başlangıçta loading bayrağını true yap
    // dispatch(setIsLoadingGetAllUsersLocation(true));
    
    // Worker oluşturuluyor.
    const worker = new Worker(new URL("../../workers/multiRegionBoundariesFilterWorker.js", import.meta.url));
    workerRef.current = worker;
    
    // Worker'a mesaj gönderiyoruz: tüm marker verileri ve tüm region verileri.
    worker.postMessage({
      markers:allUsersLocationList,
      polygons: regionsBoundariesList,
    });
    
    worker.onmessage = (e) => {
      // Eğer gelen markers dizisi boşsa veya beklenmeyen bir sonuçsa, işlemi göz ardı edebilirsiniz.
      if (!e.data || e.data.markers.length === 0 || e.data.polygons.length === 0) {
        // Boş sonuç: İstediğiniz gibi ignore edin.
        return;
      }
      console.log(e);

      dispatch(setRegionsUserCounts(e.data.polygons))
      dispatch(setUserBoundariesList({data: e.data.markers.map(item => {
        return {
          insideBoundaryRegionId: item?.insideBoundary,
          id: item?.id
        }
      }),
      type: "region"
    }))
      
      worker.terminate();
      workerRef.current = null;
    };
  }

    
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, [allUsersLocationList, regionsBoundariesList, dispatch]);

  const handleClick = useCallback(
    (item) => {
      // Eğer hâlihazırda bir worker çalışıyorsa, yeni bir tıklama ile yeni worker oluşturmayalım.
      if (workerRef.current) return;

      dispatch(
        setSelectedRegion({
          regionId: item.regionId,
          id: item.id,
          regionName: item.regionName,
          centerCoords: item.centerCoords,
        })
      );
      dispatch(setSelectedCity(null));
      dispatch(setSelectedTown(null));
      dispatch(getCitiesBoundaries({ regionId: item.regionId }));
      setHoveredRegion(null);

      //! burası değişebilir (insideBoundaryRegionId kısmı sıkıntı gibi)
      const insideUsersIds = userBoundariesList.filter(userItem => item.regionId === userItem.insideBoundaryRegionId).map(userItem => userItem.id)
      
      dispatch(setFilteredUsersLocations({data: allUsersLocationList.filter(userItem => insideUsersIds.includes(userItem.id)), type: "filter"}))
    },
    [dispatch, allUsersLocationList, userBoundariesList]
  );

  const getDynamicWidthContent = useCallback(({regionName, userCount}) => {

    if(!regionName || !userCount){
      return;
    }
    const text1 = regionName;
    const text2 = userCount?.toString();

  // Padding değerleri: sol, üst, sağ, alt
  const leftPadding = 12;
  const topPadding = 3;
  const rightPadding = 8;
  const bottomPadding = 8; // Alt padding değeri
  const lineHeight = 18; // Her satırın yüksekliği

  // İki satır için en geniş metni baz alarak genişliği hesaplıyoruz.
  const maxLength = Math.max(text1.length, text2.length);
  const width = maxLength * 8 + leftPadding + rightPadding;
  // Yükseklik: üst padding + iki satırın yüksekliği + alt padding
  const height = topPadding + lineHeight * 2 + bottomPadding;

    return (
      <div style={{ padding: 0, margin: 0 }}>
        <svg xmlns="http://www.w3.org/2000/svg" width={width} height={height}>
          <rect x="0" y="0" width={width} height={height} rx="8" ry="8" fill="black" />
          <text
            x={leftPadding}
            y={topPadding + lineHeight}
            fontSize="12"
            fill="white"
            fontFamily="Arial"
            textAnchor="start" // Sola yaslı
            fontVariant={"lining-nums"}
          >
          <tspan x={leftPadding} dy="0">{text1}</tspan>
          <tspan x={leftPadding} dy={lineHeight}>{text2} Üye</tspan>
          </text>
        </svg>
      </div>
    );
  }, []);

  if(regionsUserCounts.length !== 7){
    return null;
  }

  return (
    <>
      {regionsBoundariesList.map((item, i) =>
        item.coords.map((boundary, index) => {
          const isHovered = hoveredRegion && hoveredRegion.regionId === item.regionId;
          const isSelected = selectedRegion && selectedRegion.regionId === item.regionId;
          const polygonOptions = isHovered
            ? hoveredOptions
            : !isSelected
            ? options
            : activeOptions;

            if(selectedRegion?.regionId !== item.regionId)
          return (
            <Polygon
              key={`${item.regionId}-${index}`}
              path={boundary}
              options={polygonOptions}
              onClick={() => handleClick(item)}
              //! burada userCount u index e göre vermek mi yoksa başta bunları başka bir state koymak mı daha mantıklı emin değilim
              onMouseOver={(event) => handlePolygonMouseOver(item, regionsUserCounts[i]?.userCount, event)}
              onMouseMove={handlePolygonMouseMove}
              onMouseOut={handlePolygonMouseOut}
            />
          );
        })
      )}
      {hoveredRegion && hoveredRegion?.position && (
        <CustomInfoWindow
          position={hoveredRegion.position}
          content={getDynamicWidthContent({regionName: hoveredRegion.regionName, userCount: hoveredRegion?.userCount})}
          // content={getDynamicWidthContent({regionName: hoveredRegion.regionName, userCount: regionsUserCounts.find(item => item?.regionId === hoveredRegion?.regionId)?.userCount})}
        />
      )}
    </>
  );
};

export default RegionsBoundaries;
